/**
 * CallButton — small phone icon that triggers a native Twilio click-to-call
 * from the candidate list card or the candidate profile header.
 *
 * Falls back to a plain tel: link if the user's role doesn't allow calling
 * (referent terrain, etc.) so nobody sees a broken button.
 */
import { Loader2, Phone } from "lucide-react";
import { useCallback } from "react";
import { toast } from "sonner";

import { useCallCenter } from "../../lib/callCenter";
import { toE164 } from "../../lib/format";

export default function CallButton({
    candidate,
    variant = "icon", // "icon" | "wide"
    className = "",
    dataTestid,
}) {
    const { canCall, startOutboundCall, activeCall, status, registering } = useCallCenter();
    const busy = registering || status === "connecting" || status === "ringing";
    const inCall = !!activeCall;

    const handleClick = useCallback(
        async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!candidate?.phone) {
                toast.error("Ce candidat n'a pas de numéro de téléphone");
                return;
            }
            if (inCall) {
                toast.warning("Vous êtes déjà en communication");
                return;
            }
            await startOutboundCall(candidate);
        },
        [candidate, inCall, startOutboundCall],
    );

    // Fallback: role can't call → open the system dialer
    if (!canCall) {
        const tel = toE164(candidate?.phone);
        return (
            <a
                href={tel ? `tel:${tel}` : "#"}
                data-testid={dataTestid}
                className={
                    variant === "wide"
                        ? `h-9 inline-flex items-center gap-1.5 rounded-full px-3.5 bg-shariti-orange text-white hover:bg-[#d66528] font-bold text-xs transition shadow-btn ${className}`
                        : `h-9 w-9 inline-flex items-center justify-center rounded-full bg-shariti-orange text-white hover:bg-[#d66528] transition ${className}`
                }
                title="Appeler (via l'app système)"
            >
                <Phone className="h-4 w-4" />
                {variant === "wide" && <span>Appeler</span>}
            </a>
        );
    }

    if (variant === "wide") {
        return (
            <button
                type="button"
                onClick={handleClick}
                disabled={busy || inCall}
                data-testid={dataTestid}
                title={inCall ? "Appel déjà en cours" : "Appel rapide via PREREC (sans scorecard)"}
                className={`h-9 inline-flex items-center gap-1.5 rounded-full px-3.5 bg-emerald-500 text-white hover:bg-emerald-600 font-bold text-xs transition shadow-btn disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
            >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />}
                <span>{busy ? "Connexion…" : "Appel rapide"}</span>
            </button>
        );
    }

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={busy || inCall}
            data-testid={dataTestid}
            title={inCall ? "Appel déjà en cours" : "Appeler via PREREC (mic navigateur)"}
            className={`h-9 w-9 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white hover:bg-emerald-600 transition disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
        </button>
    );
}
